/**
 * Free "Built On Purpose" masterclass session schedule.
 *
 * Dates are PLACEHOLDERS until each cohort is scheduled. Override without a
 * code change via env:
 *   VITE_FREE_MASTERCLASS_START = 2026-09-10T18:00:00-05:00
 *   VITE_FREE_MASTERCLASS_END   = 2026-09-10T19:30:00-05:00
 *
 * Keep startDate/endDate in ISO 8601 WITH an offset — Google rejects naive
 * local times for Event rich results.
 */

const SITE_URL = "https://drsullyspeaks.com";

/** Placeholder cadence: next session, 90 minutes, US Central. */
const PLACEHOLDER_START = "2026-09-10T18:00:00-05:00";
const PLACEHOLDER_END = "2026-09-10T19:30:00-05:00";

const env = (import.meta as unknown as { env?: Record<string, string | undefined> }).env ?? {};

export const FREE_MASTERCLASS = {
  name: "Free Built On Purpose Masterclass",
  description:
    "A free live 90-minute masterclass with Dr. Sully (PsyD · ThD · DMin · CERC) introducing the 10-pillar MAKECHANGE framework: momentum, authority, clarity, healing, and the action plan that follows.",
  /** Anchor for the on-page registration form. */
  registrationUrl: `${SITE_URL}/masterclass#register`,
  pageUrl: `${SITE_URL}/masterclass`,
  image: `${SITE_URL}/og/masterclass.jpg`,
  startDate: env.VITE_FREE_MASTERCLASS_START || PLACEHOLDER_START,
  endDate: env.VITE_FREE_MASTERCLASS_END || PLACEHOLDER_END,
  durationIso: "PT1H30M",
  timeZone: "America/Chicago",
} as const;

/** Human-readable schedule line for the page (kept in sync with the markup). */
export function formatMasterclassSchedule(start: string = FREE_MASTERCLASS.startDate): string {
  const d = new Date(start);
  if (Number.isNaN(d.getTime())) return "Date to be announced";
  return new Intl.DateTimeFormat("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    timeZone: FREE_MASTERCLASS.timeZone,
    timeZoneName: "short",
  }).format(d);
}

/** schema.org Event for the free live session (online, free registration). */
export function buildFreeMasterclassEventJsonLd(id = `${FREE_MASTERCLASS.pageUrl}#free-event`) {
  return {
    "@type": "Event",
    "@id": id,
    name: FREE_MASTERCLASS.name,
    description: FREE_MASTERCLASS.description,
    url: FREE_MASTERCLASS.registrationUrl,
    image: FREE_MASTERCLASS.image,
    startDate: FREE_MASTERCLASS.startDate,
    endDate: FREE_MASTERCLASS.endDate,
    eventStatus: "https://schema.org/EventScheduled",
    eventAttendanceMode: "https://schema.org/OnlineEventAttendanceMode",
    inLanguage: "en",
    isAccessibleForFree: true,
    location: {
      "@type": "VirtualLocation",
      url: FREE_MASTERCLASS.registrationUrl,
    },
    organizer: {
      "@type": "Organization",
      name: "Dr. Sully Speaks",
      url: SITE_URL,
    },
    performer: {
      "@type": "Person",
      name: "Dr. Sully",
      url: SITE_URL,
    },
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "USD",
      availability: "https://schema.org/InStock",
      category: "Free",
      url: FREE_MASTERCLASS.registrationUrl,
      validFrom: new Date("2026-01-01T00:00:00-06:00").toISOString(),
    },
  };
}

/** Free online CourseInstance mirroring the same session, for Course results. */
export function buildFreeMasterclassCourseInstanceJsonLd() {
  return {
    "@type": "CourseInstance",
    courseMode: "Online",
    courseWorkload: FREE_MASTERCLASS.durationIso,
    inLanguage: "en",
    startDate: FREE_MASTERCLASS.startDate,
    endDate: FREE_MASTERCLASS.endDate,
    location: { "@type": "VirtualLocation", url: FREE_MASTERCLASS.registrationUrl },
    instructor: { "@type": "Person", name: "Dr. Sully", url: SITE_URL },
  };
}
